import React, { useState, useEffect } from 'react';
import { FaHeart, FaUserFriends, FaEye, FaEnvelope, FaBell } from 'react-icons/fa';
import api from '../../services/api';

const Home = ({ setActiveTab }) => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const response = await api.get('/dashboard/home');
                setDashboardData(response.data);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    // Helper function to get correct image URL
    const getProfileImage = (img, gender) => {
        if (!img) {
            return gender === 'Female'
                ? 'https://randomuser.me/api/portraits/women/85.jpg'
                : 'https://randomuser.me/api/portraits/men/85.jpg';
        }

        // If it's already a full URL (http, data:, blob:)
        if (img.startsWith('http') || img.startsWith('data:') || img.startsWith('blob:')) {
            return img;
        }

        // Relative path — prepend API base URL
        const BASE_URL = api.defaults.baseURL;
        const baseUrl = BASE_URL.replace("/api", "");
        const cleanPath = img.startsWith('/') ? img : `/${img}`;
        return `${baseUrl}${cleanPath}`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    if (!dashboardData) {
        return (
            <div className="text-center py-20">
                <p className="text-gray-600">Unable to load dashboard data</p>
            </div>
        );
    }

    const stats = [
        {
            title: "New Matches",
            count: dashboardData.stats.newMatches,
            icon: FaHeart,
            color: "text-rose-500",
            bg: "bg-rose-100"
        },
        {
            title: "Profile Views",
            count: dashboardData.stats.profileViews,
            icon: FaEye,
            color: "text-blue-500",
            bg: "bg-blue-100"
        },
        {
            title: "Interests Received",
            count: dashboardData.stats.interestsReceived,
            icon: FaUserFriends,
            color: "text-purple-500",
            bg: "bg-purple-100"
        },
        {
            title: "Unread Messages",
            count: dashboardData.stats.unreadMessages,
            icon: FaEnvelope,
            color: "text-green-500",
            bg: "bg-green-100"
        },
    ];

    const getNotificationColor = (color) => {
        const colors = {
            rose: 'bg-rose-500',
            blue: 'bg-blue-500',
            green: 'bg-green-500',
            purple: 'bg-purple-500'
        };
        return colors[color] || 'bg-gray-500';
    };

    return (
        <div className="space-y-8">
            {/* Welcome Banner */}
            <div className="bg-gradient-to-r from-rose-500 to-purple-600 rounded-3xl p-8 text-white shadow-lg">
                <h1 className="text-3xl font-bold mb-2">
                    Welcome back, {dashboardData.user.name}!
                </h1>
                <p className="opacity-90">
                    You have {dashboardData.stats.newMatches} new match{dashboardData.stats.newMatches !== 1 ? 'es' : ''} waiting for you today.
                    {dashboardData.stats.profileViews > 0 && ` Your profile was viewed ${dashboardData.stats.profileViews} time${dashboardData.stats.profileViews !== 1 ? 's' : ''}!`}
                </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                    <div key={index} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center space-x-4 hover:shadow-md transition-shadow">
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl ${stat.bg} ${stat.color}`}>
                            <stat.icon />
                        </div>
                        <div>
                            <h3 className="text-2xl font-bold text-gray-800">{stat.count}</h3>
                            <p className="text-sm text-gray-500 font-medium">{stat.title}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Recent Matches & Notifications */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Recent Matches */}
                <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-bold text-gray-800">Recent Matches</h3>
                        <button
                            onClick={() => setActiveTab('matches')}
                            className="text-rose-600 font-bold text-sm hover:underline"
                        >
                            View All
                        </button>
                    </div>
                    {dashboardData.recentMatches.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <p>No matches available yet</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {dashboardData.recentMatches.map(profile => (
                                <div
                                    key={profile.id || profile._id}
                                    className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-rose-50 transition-colors cursor-pointer group"
                                    onClick={() => window.location.href = `#/profile/${profile.id || profile._id}`}
                                >
                                    <img
                                        src={getProfileImage(profile.img, profile.gender)}
                                        alt={profile.name}
                                        onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = profile.gender === 'Female'
                                                ? 'https://randomuser.me/api/portraits/women/85.jpg'
                                                : 'https://randomuser.me/api/portraits/men/85.jpg';
                                        }}
                                        className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm group-hover:scale-105 transition-transform"
                                    />
                                    <div className="ml-4 flex-1">
                                        <h4 className="font-bold text-gray-900 truncate">{profile.name}</h4>
                                        <p className="text-sm text-gray-500">
                                            {profile.age ? `${profile.age} yrs, ` : ''}{profile.location || 'Location not specified'}
                                        </p>
                                        {profile.matchScore && (
                                            <span className="text-xs text-rose-500 font-bold mt-1 block">
                                                {profile.matchScore}% Match
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Notifications */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-bold text-gray-800">Notifications</h3>
                        <button className="text-gray-400 hover:text-gray-600">
                            <FaBell />
                        </button>
                    </div>
                    {dashboardData.notifications.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <p className="text-sm">No notifications yet</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {dashboardData.notifications.map((notification, index) => (
                                <div
                                    key={index}
                                    className={`flex items-start space-x-3 ${index !== dashboardData.notifications.length - 1 ? 'pb-4 border-b border-gray-50' : ''}`}
                                >
                                    <div className={`w-2 h-2 mt-2 rounded-full shrink-0 ${getNotificationColor(notification.color)}`}></div>
                                    <div>
                                        <p className="text-sm text-gray-800 font-medium">{notification.message}</p>
                                        <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Home;